import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { subscriptionService } from '../services';
import addressService, { AddressWithId } from '../services/addressService';
import { Address } from '../types/api';
import { paymentService } from '../services/paymentService';
import { toast } from 'react-hot-toast';
import { Gift, Wallet, MapPin, CreditCard, QrCode, Building2, ShoppingCart, DollarSign } from 'lucide-react';
import { PaymentMethod } from '../stores/subscriptionStore';

// Import components
import GiftHeader from '../components/subscription/GiftHeader';
import PageTitle from '../components/subscription/PageTitle';
import AddressAndScheduleSection from '../components/subscription/AddressAndScheduleSection';
import RecipientInfoSection from '../components/subscription/RecipientInfoSection';
import PaymentMethodsSection from '../components/subscription/PaymentMethodsSection';
import OrderSummarySection from '../components/subscription/OrderSummarySection';
import { useCurrencyState } from '../stores/subscriptionSelectors';
import { CurrencySection } from '../components/subscription/CurrencySection';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../utils/priceFormat';
import PaymentErrorDisplay from '../components/ui/PaymentErrorDisplay';
import { formatErrorForDisplay } from '../utils/paymentErrorHandler';

// SVG Icons
const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const SmallCheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-black">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const GiftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-600">
    <rect x="3" y="8" width="18" height="4" rx="1"></rect>
    <path d="M12 8v13"></path>
    <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"></path>
    <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"></path>
  </svg>
);

const ArrowLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m12 19-7-7 7-7"></path>
    <path d="M19 12H5"></path>
  </svg>
);

const QrCodeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="5" height="5" x="3" y="3" rx="1" />
    <rect width="5" height="5" x="16" y="3" rx="1" />
    <rect width="5" height="5" x="3" y="16" rx="1" />
    <path d="M21 16h-3a2 2 0 0 0-2 2v3" />
    <path d="M21 21v.01" />
    <path d="M12 7v3a2 2 0 0 1-2 2H7" />
    <path d="M3 12h.01" />
    <path d="M12 3h.01" />
    <path d="M12 16v.01" />
    <path d="M16 12h1" />
    <path d="M21 12v.01" />
    <path d="M12 21v-1" />
  </svg>
);

// Define steps for the gift redemption process
const steps = ['Validate Code', 'Choose Address', 'Complete Redemption'];
// Define steps for the gift code purchase process
const purchaseSteps = ['Select Plan', 'Add Details', 'Complete Purchase'];


const GiftCodeRedemption: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ code?: string }>();
  const location = useLocation();

  const { t } = useTranslation();
  
  // Get code from URL or query params
  const urlCode = params.code || new URLSearchParams(location.search).get('code') || '';

  // Check if we're in purchase mode (creating a gift code) or redemption mode
  const isPurchaseMode = location.state?.mode === 'purchase' || new URLSearchParams(location.search).get('mode') === 'purchase';

  // State for form and stepper
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [addresses, setAddresses] = useState<AddressWithId[]>([]);
  
  // Enhanced error handling state
  const [paymentError, setPaymentError] = useState<any>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Form state for redemption
  const [giftCode, setGiftCode] = useState(urlCode);
  const [codeValidated, setCodeValidated] = useState(false);
  const [giftDetails, setGiftDetails] = useState<any>(null);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [subscriptionDetails, setSubscriptionDetails] = useState<any>(null);

  // Form state for purchase
  const [selectedPlan, setSelectedPlan] = useState<'standard' | 'premium'>('standard');
  const [vases, setVases] = useState<number>(1);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [purchasedSubscription, setPurchasedSubscription] = useState<any>(null);
  const [plans, setPlans] = useState<any>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>('card');

  // Get currency state
  const { currency } = useCurrencyState();

  // Enhanced error handling functions
  const handleContactSupport = () => {
    // Open email client with support email
    window.open('mailto:support@piika.co?subject=Gift Code Issue', '_blank');
  };

  const handleDismissError = () => {
    setError('');
    setPaymentError(null);
  };

  const handleRetryAction = async () => {
    setIsRetrying(true);
    setError('');
    setPaymentError(null);
    
    try {
      if (isPurchaseMode) {
        // Retry purchase
        await handlePurchaseSubmit();
      } else {
        // Retry validation or redemption based on current step
        if (activeStep === 0) {
          await validateGiftCode(giftCode);
        } else if (activeStep === 1) {
          await handleRedeemGift();
        }
      }
    } catch (err) {
      const errorInfo = formatErrorForDisplay(err);
      setPaymentError(err);
      toast.error(`${errorInfo.title}: ${errorInfo.message}`);
    } finally {
      setIsRetrying(false);
    }
  };

  // Monitor changes to selectedPlan
  useEffect(() => {
    console.log('selectedPlan changed:', selectedPlan);
  }, [selectedPlan]);

  // Load addresses when component mounts
  useEffect(() => {
    // Check if returning from payment page with success status and gift code
    if (location.state?.paymentSuccess && location.state?.giftCode && location.state?.activateSuccessStep) {
      console.log('Returning from payment with success, showing gift code:', location.state.giftCode);
      setGeneratedCode(location.state.giftCode);
      setSuccess(true);
      setActiveStep(2); // Jump directly to success step
      
      // Get subscription details
      const fetchSubscriptionDetails = async () => {
        try {
          setPurchasedSubscription({
            plan: selectedPlan,
            status: 'active'
          });
        } catch (err) {
          console.error('Error fetching subscription details after payment:', err);
        }
      };
      
      fetchSubscriptionDetails();
      return;
    }
    
    if (!isPurchaseMode) {
      const fetchAddresses = async () => {
        try {
          const addressesData = await addressService.getAddresses();
          setAddresses(addressesData);
          
          // Set default address if available
          if (addressesData.length > 0) {
            const defaultAddress = addressesData.find(addr => addr.isDefault) || addressesData[0];
            setSelectedAddressId(defaultAddress.id);
          }
        } catch (err) {
          console.error('Error fetching addresses:', err);
        }
      };

      fetchAddresses();

      // If code is in URL, validate it automatically
      if (urlCode) {
        console.log('Found gift code in URL, auto-validating:', urlCode);
        setGiftCode(urlCode); // Ensure the input field shows the code
        // Small delay to ensure the component is fully rendered
        setTimeout(() => validateGiftCode(urlCode), 300);
      }
    } else {
      // In purchase mode, fetch plans
      const fetchPlans = async () => {
        try {
          setLoading(true);
          const plansData = await subscriptionService.getPlans() as any;
          console.log('Fetched plans data:', plansData);
          
          // Transform plans array into object with 'standard' and 'premium' keys
          // Handle the case where API returns array of plans directly
          if (Array.isArray(plansData)) {
            const formattedPlans = plansData.reduce((acc: Record<string, any>, plan: any) => {
              acc[plan.type] = {
                id: plan.id,
                price: plan.price,
                price_usd: plan.price_usd,
                original_price: plan.original_price,
                features: plan.features
              };
              return acc;
            }, {});
            // console.log('Formatted plans:', formattedPlans);
            setPlans(formattedPlans);
          } 
          // Handle case where API returns { data: [...plans] }
          else if (plansData && typeof plansData === 'object' && 'data' in plansData && Array.isArray(plansData.data)) {
            const formattedPlans = plansData.data.reduce((acc: Record<string, any>, plan: any) => {
              acc[plan.type] = {
                id: plan.id,
                price: plan.price,
                price_usd: plan.price_usd,
                original_price: plan.original_price,
                features: plan.features
              };
              return acc;
            }, {});
            // console.log('Formatted plans:', formattedPlans);
            setPlans(formattedPlans);
          } else {
            // For structured object response (ensure original_price is included if available)
            const formattedPlans = {
              standard: {
                ...(plansData.standard || {}),
                price: plansData.standard?.price || 0,
                price_usd: plansData.standard?.price_usd || 0,
                original_price: plansData.standard?.original_price,
              },
              premium: {
                ...(plansData.premium || {}),
                price: plansData.premium?.price || 0,
                price_usd: plansData.premium?.price_usd || 0,
                original_price: plansData.premium?.original_price,
              }
            };
            setPlans(formattedPlans);
          }
          
          setLoading(false);
        } catch (err) {
          console.error('Error fetching plans:', err);
          
          // Format and set detailed error
          const errorInfo = formatErrorForDisplay(err);
          setPaymentError(err);
          setError('Failed to load subscription plans. Please try again.');
          
          // Show toast with formatted error message
          toast.error(`${errorInfo.title}: ${errorInfo.message}`);
          
          setLoading(false);
        }
      };

      fetchPlans();
    }
  }, [urlCode, isPurchaseMode]);

  // Validate gift code
  const validateGiftCode = async (code: string) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Validating gift code:', code);
      const response = await subscriptionService.validateGiftCode(code);
      console.log('Validation API response:', response);
      
      // Create a properly formatted result object
      const result = {
        isValid: true,
        giftCode: response,
        subscription: response.subscription
      };
      
      console.log('Formatted validation result:', result);
      setGiftDetails(result);
      setCodeValidated(true);
      setActiveStep(1); // Move to address selection step
      setLoading(false);
    } catch (err: any) {
      console.error('Error validating gift code:', err);
      
      // Format and set detailed error
      const errorInfo = formatErrorForDisplay(err);
      setPaymentError(err);
      setError(err.message || 'Invalid gift code. Please try again.');
      
      // Show toast with formatted error message
      toast.error(`${errorInfo.title}: ${errorInfo.message}`);
      
      setLoading(false);
    }
  };

  // Handle validation form submission
  const handleValidateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted with code:', giftCode);
    validateGiftCode(giftCode);
  };

  // Handle redemption
  const handleRedeemGift = async () => {
    try {
      setLoading(true);
      setError('');
      
      const result = await subscriptionService.redeemGiftCode(giftCode, selectedAddressId);
      
      setSubscriptionDetails(result.subscription);
      setSuccess(true);
      setActiveStep(2); // Move to success step
      setLoading(false);
    } catch (err: any) {
      console.error('Error redeeming gift code:', err);
      
      // Format and set detailed error
      const errorInfo = formatErrorForDisplay(err);
      setPaymentError(err);
      setError(err.message);
      
      // Show toast with formatted error message
      toast.error(`${errorInfo.title}: ${errorInfo.message}`);
      
      setLoading(false);
    }
  };

  // Handle gift code purchase
  const handlePurchaseSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Make sure we're using the correct plan type
      const planToUse: 'standard' | 'premium' = selectedPlan === 'standard' ? 'standard' : 'premium';
      
      // Create gift code data
      const giftData = {
        plan: planToUse,
        message: giftMessage || undefined,
        phone: phoneNumber || undefined,
        currency: currency
      };
      
      console.log('Creating gift code payment with data:', giftData);
      
      // Create gift payment (no longer returns subscription and gift code)
      const result = await subscriptionService.createGiftCode(giftData);
      
      try {
        // Create payment intent with the payment_id returned
        const paymentIntent = await paymentService.createPaymentIntent(
          result.payment_id, // Use payment_id instead of subscription_id
          result.amount,
          'one_time',
          selectedPaymentMethod,
          undefined, // No promotion code
          result.currency as 'thb' | 'usd'
        );
        
        if (!paymentIntent || !paymentIntent.clientSecret) {
          throw new Error('Failed to create payment intent');
        }
        
        // Navigate to payment page based on selected payment method
        if (selectedPaymentMethod === 'card') {
          navigate('/stripe-payment', {
            state: {
              clientSecret: paymentIntent.clientSecret,
              subscriptionId: result.payment_id, // Use payment_id here
              paymentType: 'one_time',
              isGiftPayment: true, // Flag to indicate this is a gift payment
              plan: selectedPlan
            }
          });
        } else if (selectedPaymentMethod === 'qr' || selectedPaymentMethod === 'promptpay') {
          navigate('/qr-payment', {
            state: {
              clientSecret: paymentIntent.clientSecret,
              subscriptionId: result.payment_id, // Use payment_id here
              amount: result.amount,
              currency: result.currency,
              paymentType: 'one_time',
              isGiftPayment: true, // Flag to indicate this is a gift payment
              paymentMethod: selectedPaymentMethod // Pass the payment method to use in QR page
            }
          });
        }
      } catch (err) {
        console.error('Error creating payment:', err);
        
        // Format and set detailed error
        const errorInfo = formatErrorForDisplay(err);
        setPaymentError(err);
        setError(err instanceof Error ? err.message : 'Payment setup failed');
        
        // Show toast with formatted error message
        toast.error(`${errorInfo.title}: ${errorInfo.message}`);
        
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Error in gift code purchase:', err);
      
      // Format and set detailed error
      const errorInfo = formatErrorForDisplay(err);
      setPaymentError(err);
      setError(err.message || 'Failed to create gift code');
      
      // Show toast with formatted error message
      toast.error(`${errorInfo.title}: ${errorInfo.message}`);
      
      setLoading(false);
    }
  };

  // Handle next step in purchase process
  const handlePurchaseNext = () => {
    if (activeStep === purchaseSteps.length - 1) {
      handlePurchaseSubmit();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  // Validate current purchase step
  const isPurchaseStepValid = () => {
    switch (activeStep) {
      case 0: // Plan selection
        return !!selectedPlan;
      case 1: // Phone and message
        return true; // Phone and message are optional
      default:
        return true;
    }
  };

    return (
    <div className="min-h-screen bg-[#F5F5F5] pb-12">
      {/* Header */}
      <GiftHeader 
        isGift={isPurchaseMode} 
        title={isPurchaseMode ? t('subscription.gift.createGift') : t('subscription.gift.redeemGift')}
        onBack={() => {
          if (activeStep > 0) {
            setActiveStep(prevStep => prevStep - 1);
          } else {
            navigate(-1);
          }
        }}
      />

      {/* Main Content */}
      <div className="max-w-md mx-auto px-2 py-2">
        {/* Content based on mode and step */}
        {isPurchaseMode ? (
          // Purchase mode content
          <div className="space-y-6">
            {activeStep === 0 && (
              <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                <div className="px-6 pt-6 pb-4">
                  <div className="flex items-center">
                    <Gift size={20} className="text-gray-500 mr-2" />
                    <span className="text-lg font-semibold text-gray-900">{t('subscription.selectPlan')}</span>
                  </div>
                </div>
                <div className="px-6 pb-6">
                  <div className="space-y-4">
                    {[t('subscription.plansSection.standardPlan'), 
                    t('subscription.plansSection.premiumPlan')].map((plan, index: number) => {
                      // Get price based on currency
                      const isUSD = currency === 'usd';
                      const planPrice = isUSD 
                        ? plans?.[plan]?.price_usd || (plans?.[plan]?.price ? plans[plan].price / 35 : 0)
                        : plans?.[plan]?.price || 0;
                      
                      // Get currency symbol
                      const currencySymbol = isUSD ? '$' : '฿';
                      
                      return (
                        <div 
                          key={index}
                          onClick={() => {
                            console.log('Plan selected:', plan);
                            setSelectedPlan(plan as 'standard' | 'premium');
                          }}
                          className={`border-2 rounded-xl p-6 cursor-pointer transition-all duration-300 hover:shadow-lg ${
                            selectedPlan === plan 
                              ? 'border-[#F9D348] bg-yellow-50 shadow-md ring-2 ring-[#F9D348] ring-opacity-20' 
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center mb-4">
                                <h3 className="text-2xl font-bold text-gray-900">
                                  {plan.charAt(0).toUpperCase() + plan.slice(1)}
                                </h3>
                                {plan === 'premium' && (
                                  <span className="ml-3 px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-full">
                                    Most Popular
                                  </span>
                                )}
                              </div>
                              
                              {plans && plans[plan] && (
                                <>
                                  <div className="mb-6">
                                    <p className="text-3xl font-bold text-gray-900">
                                      {currencySymbol}{formatPrice(planPrice, currency)}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">One-time payment</p>
                                  </div>
                                  
                                  <ul className="space-y-4">
                                    {plans[plan].features && plans[plan].features.map((feature: string, index: number) => (
                                      <li key={index} className="flex items-start group">
                                        <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mr-3 mt-1 ${
                                          selectedPlan === plan ? 'bg-[#F9D348]' : 'bg-gray-100 group-hover:bg-gray-200'
                                        }`}>
                                          <span className={`text-sm ${selectedPlan === plan ? 'text-black' : 'text-gray-600'}`}>✓</span>
                                        </div>
                                        <span className="text-gray-700 group-hover:text-gray-900 transition-colors duration-200">{feature}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeStep === 1 && (
              <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                <div className="px-6 pt-6 pb-4">
                  <div className="flex items-center">
                    <Gift size={20} className="text-gray-500 mr-2" />
                    <span className="text-lg font-semibold text-gray-900">Add Contact Details</span>
                  </div>
                </div>
                <div className="px-6 pb-6">
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="phoneNumber" className="block text-base font-medium text-gray-900 mb-2">
                        Phone Number (Optional)
                      </label>
                      <input
                        id="phoneNumber"
                        type="tel"
                        className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F9D348] focus:border-transparent transition-all duration-200"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="Enter phone number"
                      />
                      <p className="mt-2 text-sm text-gray-600">You can optionally provide a phone number for notification</p>
                    </div>

                    <div>
                      <label htmlFor="giftMessage" className="block text-base font-medium text-gray-900 mb-2">
                        Gift Message (Optional)
                      </label>
                      <textarea
                        id="giftMessage"
                        rows={4}
                        className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F9D348] focus:border-transparent transition-all duration-200 resize-none"
                        value={giftMessage}
                        onChange={(e) => setGiftMessage(e.target.value)}
                        placeholder="Add a personal message to the recipient"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeStep === 2 && !success && (
              <div className="space-y-6">
                {/* Payment Error Display */}
                {(error || paymentError) && (
                  <div className="mb-6">
                    <PaymentErrorDisplay
                      error={paymentError || error}
                      onRetry={handleRetryAction}
                      onContactSupport={handleContactSupport}
                      onDismiss={handleDismissError}
                      isRetrying={isRetrying}
                    />
                  </div>
                )}
                
                <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                  <div className="px-6 pt-6 pb-2"> 
                  </div>
                  <div className="px-6 pb-6">
                    <CurrencySection />
                    
                    <PaymentMethodsSection 
                      selectedMethod={selectedPaymentMethod}
                      onSelect={setSelectedPaymentMethod}
                    />
                  </div>
                </div>

                <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                  <div className="px-6 pt-6 pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <ShoppingCart size={20} className="text-gray-500 mr-2" />
                        <span className="text-base font-medium text-gray-900">Order Summary</span>
                      </div>
                    </div>
                  </div>
                  <div className="px-6 pb-6">
                    <OrderSummarySection
                      subscriptionLevel={selectedPlan}
                      subscriptionType="monthly"
                      paymentType="one_time"
                      currentBasePriceForLevel={plans?.[selectedPlan]?.price || 0}
                      currentBasePriceForLevel_usd={plans?.[selectedPlan]?.price_usd}
                      originalRegularPriceForLevel={plans?.[selectedPlan]?.original_price || 0}
                      originalRegularPriceForLevel_usd={undefined}
                      selectedPaymentMethod={selectedPaymentMethod}
                      onTotalCalculated={() => {}}
                    />
                  </div>
                </div>

                {/* Purchase button moved outside */}
                <div className="mt-6">
                  <button
                    onClick={handlePurchaseSubmit}
                    disabled={true}
                    className={`w-full h-[56px] rounded-xl font-medium text-gray-500 bg-gray-200 cursor-not-allowed`}
                  >
                    {t('subscription.comingSoon')}
                  </button>
                </div>
              </div>
            )}

            {activeStep === 2 && success && (
              <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                <div className="px-6 py-8 text-center">
                  <div className="flex justify-center mb-6">
                    <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center shadow-sm">
                      <CheckCircleIcon />
                    </div>
                  </div>
                  
                  <h2 className="text-2xl font-semibold mb-3 text-gray-900">
                    Gift Code Generated!
          </h2>
          
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Share this code with the recipient to redeem their flower subscription.
                  </p>
                  
                  <div className="border-2 border-dashed border-[#F9D348] rounded-xl p-6 bg-yellow-50 max-w-md mx-auto">
                    <p className="font-bold text-2xl mb-3 text-yellow-700">{generatedCode}</p>
                    <p className="text-sm text-gray-600">
                      Recipient can redeem at: {window.location.origin}/gift/{generatedCode}
                    </p>
                  </div>
                  
                  <div className="mt-8">
                    <h3 className="font-medium mb-4 text-gray-900">QR Code</h3>
                    <div className="flex justify-center">
                      <div className="p-4 bg-white rounded-xl shadow-sm">
                        <img 
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(window.location.origin + '/gift/' + generatedCode)}`} 
                          alt="Gift QR Code" 
                          className="w-48 h-48 rounded-lg" 
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Redemption mode content
          <div className="space-y-6">
            {activeStep === 0 && (
              <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                <div className="px-6 pt-6 pb-4">
                  <div className="flex items-center">
                    <Gift size={24} className="text-[#F9D348] mr-3" />
                    <span className="text-lg font-semibold text-gray-900">Enter Gift Code</span>
                  </div>
                </div>
                <div className="px-6 pb-6">
                  <form onSubmit={handleValidateSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="giftCode" className="block text-sm font-medium text-gray-700 mb-2">
                        Gift Code
                      </label>
                      <input
                        id="giftCode"
                        type="text"
                        required
                        className={`w-full p-4 border-2 ${error ? 'border-red-300 focus:ring-red-500' : 'border-gray-200 focus:ring-[#F9D348]'} rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200`}
                        value={giftCode}
                        onChange={(e) => {
                          setGiftCode(e.target.value);
                          if (error) setError('');
                        }}
                        placeholder="Enter the gift code you received"
                        disabled={loading}
                      />
                      {(error || paymentError) && (
                        <div className="mt-2">
                          <PaymentErrorDisplay
                            error={paymentError || error}
                            onRetry={handleRetryAction}
                            onContactSupport={handleContactSupport}
                            onDismiss={handleDismissError}
                            isRetrying={isRetrying}
                          />
                        </div>
                      )}
                    </div>
                    
                    <button
                      type="submit"
                      disabled={!giftCode.trim() || loading}
                      className={`w-full h-[56px] rounded-xl font-medium text-black transition-all duration-200 ${
                        !giftCode.trim() || loading ? 'bg-[#FDEDB6]' : 'bg-[#F9D348] hover:bg-[#F0C83D] hover:shadow-md'
                      }`}
                      onClick={(e) => {
                        console.log('Validate button clicked');
                        if (!handleValidateSubmit) {
                          console.error('handleValidateSubmit is undefined');
                        }
                      }}
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2"></div>
                          Validating...
                        </div>
                      ) : (
                        'Validate Code'
                      )}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {activeStep === 1 && giftDetails && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                  <div className="px-6 pt-6 pb-4">
                    <div className="flex items-center">
                      <Gift size={24} className="text-[#F9D348] mr-3" />
                      <span className="text-lg font-semibold text-gray-900">Gift Details</span>
                    </div>
                  </div>
                  <div className="px-6 pb-6">
                    <div className="space-y-4">
                      <div className="border-2 border-gray-100 rounded-xl p-6 bg-gray-50">
                        <div className="space-y-3">
                          <p className="text-lg">
                            <span className="font-medium text-gray-900">Plan:</span> {giftDetails.subscription.plan.charAt(0).toUpperCase() + giftDetails.subscription.plan.slice(1)}
            </p>
            
            {giftDetails.giftCode.message && giftDetails.giftCode.message !== "Test message" && (
              <div className="mt-4">
                              <p className="font-medium mb-2 text-gray-900">
                  Message from sender:
                </p>
                              <p className="italic text-gray-700 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                  "{giftDetails.giftCode.message}"
                </p>
              </div>
            )}
                        </div>
                      </div>
                    </div>
          </div>
        </div>
        
                <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                  <div className="px-6 pt-6 pb-4">
                    <div className="flex items-center">
                      <MapPin size={24} className="text-[#F9D348] mr-3" />
                      <span className="text-lg font-semibold text-gray-900">Select Delivery Address</span>
                    </div>
                  </div>
                  <div className="px-6 pb-6">
        {addresses.length > 0 ? (
                      <div className="space-y-4">
            {addresses.map((address) => (
              <div 
                key={address.id}
                onClick={() => setSelectedAddressId(address.id)}
                            className={`border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 hover:shadow-md ${
                  selectedAddressId === address.id 
                    ? 'border-[#F9D348] bg-yellow-50 shadow-sm' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex justify-between">
                  <div>
                                <p className="font-medium text-lg mb-2 text-gray-900">
                      {address.name || 'Address'}
                                  {address.isDefault && (
                                    <span className="ml-2 text-sm text-[#F9D348] bg-yellow-50 px-2 py-1 rounded-full">
                                      Default
                                    </span>
                                  )}
                                </p>
                                
                                <p className="text-gray-600 mb-2">
                      {address.contactName}, {address.contactPhone}
                    </p>
                    
                                <p className="text-gray-600">
                                  {address.street}, {address.city}, {address.state}
                    </p>
                    
                    {address.deliveryNote && (
                                  <p className="text-gray-500 mt-3 text-sm bg-gray-50 p-3 rounded-lg">
                        Note: {address.deliveryNote}
                      </p>
                    )}
                  </div>
                  
                  {selectedAddressId === address.id && (
                                <div className="bg-[#F9D348] rounded-full p-1.5 h-7 w-7 flex items-center justify-center shadow-sm">
                      <SmallCheckCircleIcon />
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            <button
              onClick={() => navigate('/address')}
                          className="w-full py-4 border-2 border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-all duration-200 mt-2"
            >
              Add New Address
            </button>
          </div>
        ) : (
          <div className="space-y-4">
                        <div className="p-4 bg-blue-50 rounded-xl border-2 border-blue-100">
              <p className="text-blue-800 text-sm">
                You need to add an address to redeem your gift.
              </p>
            </div>
            
            <button
              onClick={() => navigate('/address')}
                          className="w-full h-[56px] rounded-xl font-medium text-black bg-[#F9D348] hover:bg-[#F0C83D] hover:shadow-md transition-all duration-200"
            >
              Add Address
            </button>
          </div>
        )}
                  </div>
                                </div>
        
        {/* Error Display for Redemption Step */}
        {(error || paymentError) && activeStep === 1 && (
          <div className="mb-6">
            <PaymentErrorDisplay
              error={paymentError || error}
              onRetry={handleRetryAction}
              onContactSupport={handleContactSupport}
              onDismiss={handleDismissError}
              isRetrying={isRetrying}
            />
          </div>
        )}
        
          <button
            onClick={handleRedeemGift}
            disabled={!selectedAddressId || loading}
            className={`w-full h-[56px] rounded-xl font-medium text-black transition-all duration-200 ${
              !selectedAddressId || loading ? 'bg-[#FDEDB6]' : 'bg-[#F9D348] hover:bg-[#F0C83D] hover:shadow-md'
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2"></div>
                Processing...
              </div>
            ) : (
              'Redeem Gift'
            )}
          </button>
      </div>
            )}

            {activeStep === 2 && subscriptionDetails && (
              <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                <div className="px-6 py-8 text-center">
                  <div className="flex justify-center mb-6">
                    <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center shadow-sm">
            <CheckCircleIcon />
          </div>
        </div>
        
                  <h2 className="text-2xl font-semibold mb-3 text-gray-900">
          Gift Successfully Redeemed!
        </h2>
        
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
          Your flower subscription has been activated.
        </p>
        
                  <div className="border-2 border-gray-100 rounded-xl p-6 bg-gray-50 max-w-md mx-auto text-left">
                    <h3 className="font-medium text-lg mb-4 text-gray-900">Subscription Details</h3>
          
          <div className="space-y-3">
                      <p className="flex justify-between border-b border-gray-200 pb-3">
                        <span className="font-medium text-gray-700">Plan:</span>
                        <span className="text-gray-900">{subscriptionDetails.plan.charAt(0).toUpperCase() + subscriptionDetails.plan.slice(1)}</span>
                      </p>
                      
                      <p className="flex justify-between border-b border-gray-200 pb-3">
                        <span className="font-medium text-gray-700">Vases:</span>
                        <span className="text-gray-900">{subscriptionDetails.vases}</span>
            </p>
            
            <p className="flex justify-between pt-1">
                        <span className="font-medium text-gray-700">Status:</span>
                        <span className="text-gray-900">{subscriptionDetails.status.charAt(0).toUpperCase() + subscriptionDetails.status.slice(1)}</span>
            </p>
          </div>
        </div>
        
        <button
          onClick={() => navigate('/subscription-management')}
                    className="w-full h-[56px] rounded-xl font-medium text-black bg-[#F9D348] hover:bg-[#F0C83D] hover:shadow-md transition-all duration-200 mt-6"
        >
          View My Subscriptions
        </button>
      </div>
                </div>
              )}
        </div>
      )}
      
        {/* Navigation buttons for purchase mode */}
        {isPurchaseMode && activeStep < 2 && (
          <div className="mt-8">
          <button
            onClick={handlePurchaseNext}
            disabled={true}
            className={`w-full h-[56px] rounded-xl font-medium text-gray-500 bg-gray-200 cursor-not-allowed`}
          >
            {t('subscription.comingSoon')}
          </button>
        </div>
      )}
        </div>
    </div>
  );
};

export default GiftCodeRedemption; 